import { XLightbox } from '@adornis/adornis-components/x-lightbox.js';
import type { Styles } from '@adornis/ass/style.js';
import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import './cr-flex.js';
import './cr-icon.js';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './cr-button.js';
import './cr-image.js';

@customElement('cr-image-carousel')
export class CrImageCarousel extends ChemistryLitElement {
  @property({ type: Number }) index = 0;
  @property({ type: Array }) imageIDs!: string[];
  private _imageFiles: Record<string, ImageFile> = {};

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          alignItems: 'center',
          width: '350px',
        },
        '::slotted(.carousel-hidden)': {
          display: 'none',
        },
        '::slotted(*)': {
          position: 'absolute',
          padding: '1em',
        },
        '#container': {
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          margin: '0 18px',
          padding: '1em',
          borderRadius: '24px',
        },
        '.big-image-item:hover': {
          cursor: 'pointer',
        },
        '.small-image-item:hover': {
          cursor: 'pointer',
        },
        '.unselected-small-image-item': {
          filter: 'brightness(75%)',
        },
      },
    ];
  }

  override connectedCallback(): void {
    super.connectedCallback();
  }

  protected override render(): Renderable {
    const imageID = this.imageIDs.at(this.index);
    if (!imageID) return nothing;
    const hasOneImage = this.imageIDs.length <= 1;
    return html`
      <cr-flex w100>
        <cr-flex horizontal ${css({ alignItems: 'center' })}>
          ${hasOneImage ? nothing : this._leftArrow()} ${this._bigImageItem(imageID, this.index)}
          ${hasOneImage ? nothing : this._rightArrow()}
        </cr-flex>
        ${this._imagePreview()}
      </cr-flex>
    `;
  }

  private _leftArrow(): Renderable {
    return html`
      <cr-flex start>
        <cr-button mode="icon" icon="chevron_left" @click=${() => this._onLeftClick()}> </cr-button>
      </cr-flex>
    `;
  }

  private _rightArrow(): Renderable {
    return html`
      <cr-flex end>
        <cr-button mode="icon" icon="chevron_right" @click=${() => this._onRightClick()}></cr-button>
      </cr-flex>
    `;
  }

  private _imagePreview(): Renderable {
    if (this.imageIDs.length <= 1) return nothing;
    return html`
      <cr-flex horizontal space="xxs" padding="sm" w100 ${css({ overflowX: 'auto' })}>
        ${this.imageIDs.map((imageID, i) => this._smallImageItem(imageID, i))}
      </cr-flex>
    `;
  }

  private _bigImageItem(imageID: string, index: number): TemplateResult<1> {
    return html`
      <cr-flex flex-none>
        <cr-image
          class="big-image-item"
          .value=${imageID}
          mode="contain"
          disabled
          ${css({ width: '300px', height: '300px' })}
          @click=${() => this._onBigImageItemClick(index)}
        ></cr-image>
      </cr-flex>
    `;
  }

  private async _onBigImageItemClick(index: number): Promise<void> {
    const files = [] as ImageFile[];
    for (const imageID of this.imageIDs) {
      if (!(imageID in this._imageFiles) || this._imageFiles[imageID] === undefined) {
        const file = await getByID(ImageFile, imageID)(ImageFile.allFields);
        if (file) this._imageFiles[imageID] = file as ImageFile;
      }
      const file = this._imageFiles[imageID];
      if (file) files.push(file);
    }
    if (files.length > 0)
      await XLightbox.showPopup({ props: { files, startingIndex: index } }).catch(err => {
        logger.error({ err }, 'Es gab ein Problem beim Anzeigen der Produktbilder in der Lightbox.');
      });
  }

  private _smallImageItem(imageID: string, index: number): TemplateResult<1> {
    const classes = `small-image-item ${
      index === this.index ? 'selected-small-image-item' : 'unselected-small-image-item'
    }`;
    return html` <cr-flex flex-none>
      <img
        class=${classes}
        @click=${() => {
          this.index = index;
        }}
        src=${ImageFile.getServeLink(imageID, { size: '300x300' })}
        ${css({ width: '50px', height: '50px' })}
      />
    </cr-flex>`;
  }

  private _onLeftClick(): void {
    this.index = (this.index + this.imageIDs.length - 1) % this.imageIDs.length;
  }

  private _onRightClick(): void {
    this.index = (this.index + 1) % this.imageIDs.length;
  }
}
